import { useEffect, useState } from 'react'
import { clipFactoryAPI, clipFactoryErrorText, type ClaimsResult, type ClipLine, type PostCopy } from './clipFactoryApi'
import { formatTime, type Range } from './clipUtils'

const DISCLAIMER = 'Not financial advice.'

interface FixPanelProps {
  videoId: string
  clipKey: string
  range: Range
  postCopy: PostCopy | undefined
  /** Claude is set up, so the post copy can be written again. */
  aiAvailable: boolean
  /** The clip talks about a trade, so "Not financial advice." stays on its caption. */
  tradeTalk: boolean
  /** Captions changed: exports with burned-in captions are out of date. */
  onCaptionsChanged: () => void
  onPostCopy: (copy: PostCopy) => void
}

/** A line's text with each suggested fix marked: "went long [soul → SOL] right". */
const Marked = ({ line }: { line: ClipLine }) => {
  const parts: JSX.Element[] = []
  let at = 0
  line.fixes.forEach((f, i) => {
    if (f.start > at) parts.push(<span key={`t${i}`}>{line.text.slice(at, f.start)}</span>)
    parts.push(
      <mark key={`f${i}`} className="rounded bg-amber-100 px-0.5 text-amber-900">
        <s>{f.heard}</s> → {f.fix}
      </mark>
    )
    at = f.end
  })
  parts.push(<span key="end">{line.text.slice(at)}</span>)
  return <>{parts}</>
}

const tagsText = (tags: string[]) => tags.map((t) => `#${t.replace(/^#/, '')}`).join(' ')
const tagsOf = (text: string) => text.split(/[\s,]+/).map((t) => t.replace(/^#/, '')).filter(Boolean)
const withoutDisclaimer = (caption: string) => caption.replace(/\s*Not financial advice\.?\s*$/i, '')

/**
 * "Fix before you post": the clip's caption lines, with coin names Whisper
 * likely misheard marked and fixable in one go, and the clip's title, caption
 * and hashtags, editable. What viewers read is what's right.
 */
const FixPanel = ({ videoId, clipKey, range, postCopy, aiAvailable, tradeTalk, onCaptionsChanged, onPostCopy }: FixPanelProps) => {
  const [lines, setLines] = useState<ClipLine[] | null>(null)
  const [drafts, setDrafts] = useState<Record<number, string>>({})
  const [busy, setBusy] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [title, setTitle] = useState('')
  const [caption, setCaption] = useState('')
  const [tags, setTags] = useState('')
  const [claims, setClaims] = useState<ClaimsResult | null>(null)
  const cutsKey = JSON.stringify(range.cuts || [])

  // The clip's lines, again when another clip is picked or the trim moves.
  useEffect(() => {
    let stopped = false
    setLines(null)
    setDrafts({})
    setMessage(null)
    setError(null)
    const timer = setTimeout(() => {
      clipFactoryAPI
        .clipLines(videoId, range.start, range.end)
        .then((r) => !stopped && setLines(r.lines))
        .catch((e) => !stopped && setError(clipFactoryErrorText(e, "Couldn't load the clip's captions.")))
    }, 300)
    return () => {
      stopped = true
      clearTimeout(timer)
    }
  }, [videoId, range.start, range.end])

  // Results said aloud, checked against FinCreator's posting rules: again
  // when the clip, its trim or its cuts change. A failure just leaves it out.
  useEffect(() => {
    let stopped = false
    setClaims(null)
    const timer = setTimeout(() => {
      clipFactoryAPI
        .claims(videoId, range.start, range.end, JSON.parse(cutsKey))
        .then((r) => !stopped && setClaims(r))
        .catch(() => {})
    }, 400)
    return () => {
      stopped = true
      clearTimeout(timer)
    }
  }, [videoId, range.start, range.end, cutsKey])

  // The post copy fields follow the clip and any copy saved elsewhere.
  useEffect(() => {
    setTitle(postCopy?.titles[0] || '')
    setCaption(withoutDisclaimer(postCopy?.caption || ''))
    setTags(tagsText(postCopy?.hashtags || []))
  }, [clipKey, postCopy])

  const fixCount = (lines || []).reduce((n, l) => n + l.fixes.length, 0)
  const claimCount = claims?.claims.length || 0
  const toCheck = fixCount + claimCount

  const run = async (what: string, job: () => Promise<void>, failed: string) => {
    setBusy(what)
    setError(null)
    setMessage(null)
    try {
      await job()
    } catch (e) {
      setError(clipFactoryErrorText(e, failed))
    } finally {
      setBusy(null)
    }
  }

  const fixAll = () =>
    run('fix', async () => {
      const r = await clipFactoryAPI.fixClip(videoId, range.start, range.end, clipKey)
      setLines(r.lines)
      setMessage(`Fixed ${r.fixed} ${r.fixed === 1 ? 'word' : 'words'} in the captions${r.elsewhere ? ', and the title and post copy to match' : ''}.`)
      onCaptionsChanged()
    }, "Couldn't fix the captions.")

  const saveLine = (line: ClipLine) => {
    const text = (drafts[line.index] ?? line.text).trim()
    if (!text || text === line.text) {
      setDrafts((prev) => {
        const next = { ...prev }
        delete next[line.index]
        return next
      })
      return
    }
    run('line', async () => {
      await clipFactoryAPI.editClipLine(videoId, line.index, text, range.start, range.end)
      const r = await clipFactoryAPI.clipLines(videoId, range.start, range.end)
      setLines(r.lines)
      setDrafts((prev) => {
        const next = { ...prev }
        delete next[line.index]
        return next
      })
      setMessage('Caption saved.')
      onCaptionsChanged()
    }, "Couldn't save the caption.")
  }

  const savedCopy = {
    titles: [title.trim(), ...(postCopy?.titles || []).slice(1)].filter(Boolean),
    caption: caption.trim(),
    hashtags: tagsOf(tags),
  }
  const copyChanged =
    title.trim() !== (postCopy?.titles[0] || '') ||
    caption.trim() !== withoutDisclaimer(postCopy?.caption || '').trim() ||
    tagsText(savedCopy.hashtags) !== tagsText(postCopy?.hashtags || [])

  const saveCopy = () =>
    run('copy', async () => {
      onPostCopy(await clipFactoryAPI.editPostCopy(videoId, clipKey, savedCopy))
      setMessage('Post copy saved.')
    }, "Couldn't save the post copy.")

  const rewrite = () =>
    run('rewrite', async () => {
      onPostCopy(await clipFactoryAPI.rewritePostCopy(videoId, clipKey, range.start, range.end))
      setMessage('Post copy written again.')
    }, "Couldn't write new post copy.")

  return (
    <details open className="rounded-md border border-gray-300 p-3 text-sm text-gray-700" data-testid="fix-panel">
      <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-gray-500">
        Fix before you post{toCheck > 0 ? ` · ${toCheck} to check` : ''}
      </summary>

      {claims && (
        <div className="mt-3 space-y-1" data-testid="spoken-claims">
          <h4 className="font-medium text-gray-900">What you say</h4>
          {!claims.checked ? (
            <p className="text-xs text-gray-500">{claims.reason}</p>
          ) : claimCount === 0 ? (
            <p className="text-xs text-gray-500">
              No results said without &ldquo;before fees&rdquo; or without their dates.
            </p>
          ) : (
            <>
              <ul className="space-y-1" aria-label="Results said in this clip">
                {claims.claims.map((c, i) => (
                  <li key={i} className="rounded bg-amber-50 px-2 py-1 text-xs text-amber-900">
                    <span className="font-medium">At {formatTime(c.time)}:</span> {c.message}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-gray-500">
                The same rules every FinCreator post is checked against. It won&rsquo;t stop the export: you decide.
              </p>
            </>
          )}
        </div>
      )}

      <div className="mt-3 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h4 className="font-medium text-gray-900">Captions</h4>
          {fixCount > 0 && (
            <button
              type="button"
              onClick={fixAll}
              disabled={busy !== null}
              className="rounded-md bg-amber-500 px-2 py-1 text-xs font-medium text-white hover:bg-amber-600 disabled:opacity-50"
            >
              {busy === 'fix' ? 'Fixing…' : `Fix ${fixCount} coin ${fixCount === 1 ? 'name' : 'names'}`}
            </button>
          )}
        </div>
        {fixCount > 0 && (
          <p className="text-xs text-gray-500">The transcript may have misheard these. Check them, then fix them all at once.</p>
        )}
        {lines === null && !error && <p className="text-xs text-gray-400">Loading captions…</p>}
        <ul className="space-y-2" aria-label="Caption lines">
          {(lines || []).map((line) => (
            <li key={line.index} className="space-y-1">
              <span className="block text-xs text-gray-400">{formatTime(line.start)}</span>
              {line.fixes.length > 0 && drafts[line.index] === undefined && (
                <p className="text-xs leading-relaxed" data-testid="caption-fixes">
                  <Marked line={line} />
                </p>
              )}
              <textarea
                aria-label={`Caption at ${formatTime(line.start)}`}
                rows={2}
                value={drafts[line.index] ?? line.text}
                onChange={(e) => setDrafts((prev) => ({ ...prev, [line.index]: e.target.value }))}
                onBlur={() => saveLine(line)}
                disabled={busy !== null && busy !== 'line'}
                className="w-full min-w-0 rounded-md border border-gray-300 px-2 py-1 text-sm"
              />
            </li>
          ))}
        </ul>
        <p className="text-xs text-gray-500">Changes are saved when you leave a line, and fix the transcript for every clip of this video.</p>
      </div>

      <div className="mt-4 space-y-2 border-t border-gray-200 pt-3">
        <div className="flex items-center justify-between gap-2">
          <h4 className="font-medium text-gray-900">Post copy</h4>
          {aiAvailable && (
            <button
              type="button"
              onClick={rewrite}
              disabled={busy !== null}
              className="rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100 disabled:opacity-50"
            >
              {busy === 'rewrite' ? 'Writing…' : 'Write again with AI'}
            </button>
          )}
        </div>
        <label className="block">
          <span className="text-xs text-gray-500">Title</span>
          <input
            type="text"
            value={title}
            maxLength={100}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full min-w-0 rounded-md border border-gray-300 px-2 py-1 text-sm"
          />
        </label>
        <label className="block">
          <span className="text-xs text-gray-500">Caption</span>
          <textarea
            rows={4}
            value={caption}
            maxLength={2200}
            onChange={(e) => setCaption(e.target.value)}
            className="w-full min-w-0 rounded-md border border-gray-300 px-2 py-1 text-sm"
          />
        </label>
        {tradeTalk && <p className="text-xs text-gray-500">"{DISCLAIMER}" is added to the end, since this clip talks about a trade.</p>}
        <label className="block">
          <span className="text-xs text-gray-500">Hashtags</span>
          <input
            type="text"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            className="w-full min-w-0 rounded-md border border-gray-300 px-2 py-1 text-sm"
          />
        </label>
        <button
          type="button"
          onClick={saveCopy}
          disabled={busy !== null || !copyChanged || (!savedCopy.titles.length && !savedCopy.caption)}
          className="rounded-md bg-primary-600 px-3 py-1 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {busy === 'copy' ? 'Saving…' : 'Save post copy'}
        </button>
      </div>

      <div aria-live="polite" className="mt-2 text-xs">
        {message && <p className="text-success-700">{message}</p>}
        {error && (
          <p role="alert" className="text-danger-700">
            {error}
          </p>
        )}
      </div>
    </details>
  )
}

export default FixPanel
